import { Component, Inject, inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { SVG_ICONS } from "@frontend/app/models/icons";
import { ToastrService } from "@frontend/app/serivces/toastr.service";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MasterService } from "@frontend/app/serivces/master.service";
import { SharedModule } from "@frontend/app/shared/shared.module";
import { MultiSelectDropdownComponent } from "@frontend/app/components/multi-select-dropdown/multi-select-dropdown.component";
import { Cls_ProductData } from "@frontend/app/models/master";
import { DocumentUploadComponent } from "@frontend/app/components/document-upload/document-upload.component";

type DocumentObject = {
  Category: string;
  FileName: string;
  MimeType: string;
  Base64: string;
  DID?: number;
};

@Component({
  selector: "app-add-product",
  imports: [CommonModule, SharedModule, MultiSelectDropdownComponent, DocumentUploadComponent],
  templateUrl: "./add-product.component.html",
  styleUrl: "./add-product.component.scss",
})
export class AddProductComponent implements OnInit {
  private masterService = inject(MasterService);
  private toastrService = inject(ToastrService);
  private dialogRef = inject(MatDialogRef<AddProductComponent>);

  get selectedSizeIDs(): number[] {
    return this.productData.Size ? this.productData.Size.map(s => s.SizeID) : [];
  }

  set selectedSizeIDs(ids: number[]) {
    this.productData.Size = ids.map(id => {
      const existing = this.productData.Size?.find(s => s.SizeID === id);
      return { SizeID: id, Quantity: existing ? existing.Quantity : 0 };
    });
  }

  constructor(@Inject(MAT_DIALOG_DATA) public data: any) {}

  ngOnInit(): void {
    if (this.data?.productId) {
      this.productData.ProductID = this.data.productId;
      this.getProductDetailsByID(this.data.productId);
    }
    this.getProductCompanyList();
    this.getProductSizeList();
  }

  //#region variables
  companyList: any;
  productData = new Cls_ProductData();
  productSizeList: any;
  isRegionDropdownOpen = false;
  isCityDropdownOpen = false;
  ErrorMsg: string[] = [];
  readonly SVG_ICONS = SVG_ICONS;

  // Track existing and new documents here
  uploadedDocuments: { [key: string]: DocumentObject[] } = {
    Image: [],
  };

  // NEW: Track IDs of documents the user removed
  deletedDocumentIds: number[] = [];
  //#endregion

  onRegionChange(regionId: number): void {
    if (this.productData) {
      this.productData.CompanyID = regionId;
    }
  }

  validateForm(): boolean {
    if (!this.productData.ProductName || !this.productData.ProductName.trim()) {
      this.toastrService.error("Product Name is required");
      return false;
    }
    if (!this.productData.CompanyID || this.productData.CompanyID <= 0) {
      this.toastrService.error("Please select a valid Company");
      return false;
    }
    if (!this.productData.Size || this.productData.Size.length === 0) {
      this.toastrService.error("Please select at least one Size");
      return false;
    }
    if (this.productData.Amount === null || this.productData.Amount === undefined || this.productData.Amount <= 0) {
      this.toastrService.error("Amount must be greater than 0");
      return false;
    }
    return true;
  }

  getProductCompanyList() {
    this.masterService.getProductCompanyList().subscribe((res: any) => {
      this.companyList = res.data;
    });
  }

  getProductSizeList() {
    this.masterService.getProductSizeList().subscribe((res: any) => {
      this.productSizeList = res.data;
    });
  }

  getProductDetailsByID(id: number) {
    this.masterService.getProductByID(id).subscribe((res: any) => {
      Object.assign(this.productData, res.data);

      if (res.data.ProductSizeInventory) {
        this.productData.Size = res.data.ProductSizeInventory.map((x: any) => ({
          SizeID: x.SizeID,
          Quantity: x.CurrentStock ?? x.Quantity ?? 0, // API returns CurrentStock
        }));
      }

      if (res.data.Documents && res.data.Documents.length > 0) {
        this.uploadedDocuments["Image"] = res.data.Documents.map((doc: any) => ({
          Category: "Image",
          FileName: doc.DocumentName || "Product Image",
          MimeType: "image/jpeg", // or dynamic if available
          Base64: doc.URL, // This is a URL, not real Base64
          DID: doc.DID,
        }));
      }
    });
  }

  updateDocuments(newDocs: DocumentObject[]): void {
    if (!newDocs || newDocs.length === 0) return;
    const category = newDocs[0].Category;
    if (category === "Profile") {
      this.uploadedDocuments[category] = [newDocs[0]];
    } else {
      if (!this.uploadedDocuments[category]) {
        this.uploadedDocuments[category] = [];
      }
      this.uploadedDocuments[category].push(...newDocs);
    }
  }

  removeFile(docToRemove: DocumentObject): void {
    const category = docToRemove.Category;

    // Logic: If it has a DID, it's an existing saved file. We need to tell backend to delete it.
    if (docToRemove.DID) {
      this.deletedDocumentIds.push(docToRemove.DID);
    }

    if (this.uploadedDocuments[category]) {
      this.uploadedDocuments[category] = this.uploadedDocuments[category].filter(doc => doc.FileName !== docToRemove.FileName);
    }
  }

  onSubmit() {
    if (!this.validateForm()) {
      return;
    }
    const tempModal = new Cls_ProductData();
    tempModal.ProductID = this.productData.ProductID || 0;
    tempModal.ProductName = this.productData.ProductName;
    tempModal.CompanyID = this.productData.CompanyID;
    tempModal.Size = this.productData.Size;
    tempModal.Amount = this.productData.Amount;
    tempModal.SGST = this.productData.SGST;
    tempModal.CGST = this.productData.CGST;
    tempModal.StudentDiscount = this.productData.StudentDiscount;
    tempModal.MinDiscount = this.productData.MinDiscount;
    tempModal.Remarks = this.productData.Remarks;
    tempModal.Description = this.productData.Description;
    tempModal.IsActive = this.productData.IsActive;

    // Map documents correctly
    tempModal.Documents = Object.entries(this.uploadedDocuments).flatMap(([category, docs]) =>
      (Array.isArray(docs) ? docs : []).map(doc => {
        const isNewFile = doc.Base64?.startsWith("data:");
        return {
          DID: doc.DID,
          Category: category,
          FileName: doc.FileName,
          MimeType: doc.MimeType,
          Base64: isNewFile ? doc.Base64.split(",")[1] : null,
        };
      })
    );
    console.log(tempModal);

    if (this.productData?.ProductID) {
      this.masterService.updateProduct(tempModal, this.productData.ProductID).subscribe((res: any) => {
        this.toastrService.success("Product updated successfully");
        this.dialogRef.close(res);
      });
    } else {
      this.masterService.addProduct(tempModal).subscribe((res: any) => {
        this.toastrService.success("Product added successfully");
        this.dialogRef.close(res);
      });
    }
  }

  getSizeName(sizeId: number): string {
    const size = this.productSizeList?.find((x: any) => x.SizeID === sizeId);
    return size ? size.Size : "";
  }

  onClose() {
    this.dialogRef.close();
  }
}
``;
